import { 
  CashFlowResult, 
  CompetitiveResult, 
  DebtResult, 
  ExpenseResult,
  InventoryResult,
  PayablesResult,
  ScenarioResult,
  TaxResult
} from '../types';
import { modelStories } from '../data/stories';

// Simulate API delay
const delay = (ms: number = 2000) => new Promise(resolve => setTimeout(resolve, ms));

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Generate mock data with story integration
class EnhancedAPI {
  async optimizeCashFlow(params: {
    company_id: string;
    planning_horizon_days: number;
    minimum_cash_balance: number;
  }): Promise<CashFlowResult> {
    await delay(2500);
    
    const schedule = Array.from({ length: params.planning_horizon_days }, (_, i) => ({
      period: i + 1,
      date: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      opening_balance: 45000000 + Math.random() * 5000000,
      inflows: 8000000 + Math.random() * 2000000,
      outflows: 7500000 + Math.random() * 1500000,
      investments: Math.random() * 1000000,
      borrowings: Math.random() < 0.3 ? Math.random() * 500000 : 0,
      closing_balance: 46000000 + Math.random() * 4000000,
    }));

    return {
      status: 'optimal',
      objective_value: 48750000,
      solve_time_seconds: 2.34,
      story: modelStories.cashflow,
      model_info: {
        algorithm: 'Linear Programming (OR-Tools GLOP)',
        solver: 'GLOP',
        variables: 240,
        constraints: 360,
        iterations: 1247,
        confidence: 0.96,
      },
      input_summary: {
        company_id: params.company_id,
        planning_horizon: `${params.planning_horizon_days} days`,
        minimum_balance: formatCurrency(params.minimum_cash_balance),
        transactions_analyzed: '18,250',
        data_period: 'Last 12 months',
        forecast_method: 'ARIMA + Seasonal Decomposition',
      },
      llm_explanation: `Our Linear Programming optimization analyzed ${params.planning_horizon_days} days of cash flow projections for ${params.company_id}. The model identified optimal allocation strategies across investment opportunities and credit facilities while maintaining the required ${formatCurrency(params.minimum_cash_balance)} minimum balance.

Key findings: The current cash position can be optimized by reducing idle cash from $75M to $45M, investing the freed capital in short-term instruments yielding 4.2%, and strategically utilizing credit facilities only when the cost is below investment returns. The optimization suggests maintaining an average balance of $46.2M, which provides adequate liquidity while maximizing returns.

Risk assessment: Liquidity risk is LOW with 15% buffer above minimum requirements. The recommended strategy maintains 100% compliance with all covenant requirements while improving capital efficiency by 38%.`,
      recommendations: [
        'Reduce cash buffer from $75M to $45M, freeing $30M for strategic investments',
        'Invest freed capital in money market funds and short-term treasuries (4.2% yield)',
        'Utilize credit facilities only when borrowing cost < investment returns',
        'Implement daily cash position monitoring with automated rebalancing',
        'Negotiate improved terms on revolving credit facility (target: 5.5% from 6.2%)',
      ],
      risk_metrics: {
        'Liquidity Risk': 'LOW',
        'Concentration Risk': 'LOW',
        'Interest Rate Sensitivity': 'MODERATE',
        'Forecast Accuracy': 'HIGH (92%)',
      },
      cash_flow_schedule: schedule,
      summary: {
        ending_balance: schedule[schedule.length - 1].closing_balance,
        minimum_balance: Math.min(...schedule.map(s => s.closing_balance)),
        average_balance: schedule.reduce((sum, s) => sum + s.closing_balance, 0) / schedule.length,
        total_inflows: schedule.reduce((sum, s) => sum + s.inflows, 0),
        total_outflows: schedule.reduce((sum, s) => sum + s.outflows, 0),
        total_investments: schedule.reduce((sum, s) => sum + s.investments, 0),
        total_borrowings: schedule.reduce((sum, s) => sum + s.borrowings, 0),
        investment_returns: 1800000,
        borrowing_costs: 660000,
        net_benefit: 2900000,
      },
    };
  }

  async analyzeCompetition(): Promise<CompetitiveResult> {
    await delay(2000);
    
    return {
      status: 'completed',
      story: modelStories.competitive,
      model_info: {
        algorithm: 'Market Share Analysis + Porter\'s Five Forces',
        variables: 156,
        constraints: 89,
        confidence: 0.88,
      },
      input_summary: {
        market_size: '$500M',
        competitors_analyzed: '3 major + 15 minor',
        data_sources: 'G2, Capterra, Web scraping (500K+ data points)',
        time_period: 'Q3 2025',
        pricing_data_points: '3,400 deals',
      },
      llm_explanation: `Comprehensive competitive analysis reveals TaskFlow holds a strong position in the mid-market segment (500-2,000 employees) with 34% market share, significantly above the 28.5% overall average. The pricing analysis using 3,400+ historical deals shows price elasticity of -1.8, indicating the company is in the elastic demand zone where small price increases can improve revenue without significant volume loss.

Porter's Five Forces assessment indicates MODERATE competitive intensity (score: 6.2/10). Threat of new entrants is LOW due to high switching costs and network effects. Bargaining power of buyers is MODERATE as enterprise customers have alternatives but face high switching costs. The analysis identified a "value trap" where pricing too low signals inferior quality despite delivering enterprise-grade features.

Strategic recommendation: Increase pricing to $154.99 (3.3% increase) with segmented enterprise tier at $179.99. This positions TaskFlow as a premium mid-market solution while capturing additional value from enterprise customers. Expected impact: $8.7M revenue increase with <1% customer loss.`,
      recommendations: [
        'Increase base pricing to $154.99 per user/month (3.3% increase)',
        'Launch enterprise tier at $179.99 for companies with 1,000+ employees',
        'Focus sales efforts on 500-2,000 employee segment (34% market share)',
        'Enhance competitive positioning messaging around enterprise-grade features',
        'Implement value-based pricing with ROI calculators for prospects',
      ],
      market_share: {
        company: 31.2,
        competitor_a: 28.5,
        competitor_b: 22.3,
        others: 18.0,
      },
      pricing: {
        current: 149.99,
        competitor_avg: 159.99,
        recommended: 154.99,
        elasticity: -1.8,
      },
      position: {
        status: 'Strong in mid-market',
        growth_rate: 24.5,
        profitability: 85.2,
        competitive_intensity: 6.2,
      },
    };
  }

  async optimizeDebt(): Promise<DebtResult> {
    await delay(2200);
    
    return {
      status: 'optimal',
      total_savings: 1150000,
      story: modelStories.debt,
      model_info: {
        algorithm: 'Mixed-Integer Linear Programming (MILP)',
        solver: 'CBC',
        variables: 48,
        constraints: 127,
        iterations: 3421,
        confidence: 0.94,
      },
      input_summary: {
        total_debt: '$85M',
        facilities: '4 (Term loan, Revolver, Bond, Equipment)',
        current_rate: '5.65% weighted average',
        credit_rating: 'BBB+',
        scenarios_evaluated: '10,000+',
      },
      llm_explanation: `The MILP optimization evaluated 10,000+ refinancing scenarios for TitanForge's $85M debt portfolio. The analysis identified significant opportunities to reduce financing costs by leveraging the improved credit rating (BB+ to BBB+) and favorable market conditions.

Key findings: The $35M term loan at 6.5% can be refinanced at 4.5% (current market rate for BBB+ credits), saving $700K annually. The $20M bond at 5.5% can be refinanced at 4.8%, saving $140K annually. After accounting for $450K in transaction costs (legal, banking fees, prepayment penalties), the NPV of savings over the debt lifecycle is $8.7M.

The optimization also rebalanced the maturity profile, eliminating the concentration of $30M maturing in 2025-2026. The new structure has no maturities before 2027, significantly reducing refinancing risk. DSCR improved from 1.35x to 1.52x, providing substantial covenant cushion for strategic flexibility.`,
      recommendations: [
        'Refinance $35M term loan from 6.5% to 4.5% (save $700K annually)',
        'Refinance $20M bond from 5.5% to 4.8% (save $140K annually)',
        'Extend revolving credit facility to 2029 with improved terms (5.8% to 5.2%)',
        'Consolidate equipment financing into term loan structure for simplicity',
        'Use $2.3M annual cash flow savings to invest in automation and R&D',
      ],
      facilities: [
        { name: 'Term Loan (Refinanced)', amount: 35000000, rate: 4.5, term: 60, savings: 700000 },
        { name: 'Revolving Credit', amount: 25000000, rate: 5.2, term: 48, savings: 150000 },
        { name: 'Bond (Refinanced)', amount: 20000000, rate: 4.8, term: 84, savings: 140000 },
        { name: 'Equipment Financing', amount: 5000000, rate: 4.5, term: 36, savings: 160000 },
      ],
    };
  }

  async detectAnomalies(): Promise<ExpenseResult> {
    await delay(2300);
    
    return {
      status: 'completed',
      total: 2300000,
      detected: 1847,
      high_risk: 23,
      medium_risk: 156,
      low_risk: 1668,
      story: modelStories.expenses,
      model_info: {
        algorithm: 'Ensemble ML (Isolation Forest + Autoencoder + SPC)',
        confidence: 0.91,
      },
      input_summary: {
        transactions_analyzed: '2,300,000',
        time_period: 'Q3 2025',
        total_amount: '$1.2B',
        categories: '47',
        vendors: '8,500',
      },
      llm_explanation: `The ensemble machine learning system analyzed 2.3M expense transactions totaling $1.2B across 47 categories. The system detected 1,847 anomalies (0.08% of transactions) with 91% precision, identifying $4.2M in fraud and policy violations.

High-risk anomalies (23 transactions, $1.8M): Include duplicate vendor payments, personal expenses on corporate cards, and a potential kickback scheme involving 3 employees and 2 vendors. These require immediate investigation and remediation.

Medium-risk anomalies (156 transactions, $1.1M): Policy violations such as unapproved vendors, excessive entertainment expenses, and transactions just under approval thresholds. These suggest process improvements and enhanced controls.

The system's 9% false positive rate (vs. 40-50% for rule-based systems) minimizes review burden while maintaining high detection accuracy. The deterrent effect has been significant - policy violations dropped 73% once employees knew the system was monitoring all transactions.`,
      recommendations: [
        'Immediately investigate 23 high-risk anomalies ($1.8M) - potential fraud',
        'Review medium-risk transactions for policy violations and process improvements',
        'Implement automated vendor duplicate checking before payment processing',
        'Enhance approval workflows for transactions near threshold limits',
        'Conduct training on expense policy compliance for all employees',
        'Consider implementing pre-approval requirements for high-risk categories',
      ],
      risk_metrics: {
        'Detection Accuracy': 'HIGH (91%)',
        'False Positive Rate': 'LOW (9%)',
        'Coverage': 'COMPLETE (100%)',
        'Response Time': 'REAL-TIME',
      },
      anomalies: [
        { id: 'EXP-2025-08471', amount: 145000, category: 'Professional Services', score: 0.96, reason: 'Duplicate payment to same vendor', impact: 'High' },
        { id: 'EXP-2025-12093', amount: 89000, category: 'Marketing', score: 0.94, reason: 'Personal expense on corporate card', impact: 'High' },
        { id: 'EXP-2025-15672', amount: 67000, category: 'IT Services', score: 0.91, reason: 'Vendor not in approved list', impact: 'Medium' },
        { id: 'EXP-2025-18234', amount: 54000, category: 'Entertainment', score: 0.89, reason: 'Excessive amount for category', impact: 'Medium' },
        { id: 'EXP-2025-20156', amount: 49900, category: 'Office Supplies', score: 0.87, reason: 'Just under approval threshold', impact: 'Medium' },
      ],
    };
  }

  async optimizeInventory(): Promise<InventoryResult> {
    await delay(2100);
    
    return {
      status: 'optimal',
      savings: 6750000,
      story: modelStories.inventory,
      model_info: {
        algorithm: 'EOQ + (Q,R) Policy with ML Forecasting',
        confidence: 0.92,
      },
      input_summary: {
        skus: '47,000',
        distribution_centers: '8',
        current_inventory: '$145M',
        turnover_rate: '2.0x annually',
        forecast_accuracy: '92%',
      },
      llm_explanation: `The inventory optimization system analyzed 47,000 SKUs across 8 distribution centers using Economic Order Quantity (EOQ) and (Q,R) policy optimization enhanced with machine learning demand forecasting.

Key findings: Current inventory of $145M represents 180 days of supply (vs. 120-day industry benchmark), tying up $36M in excess capital. The optimization identified opportunities to reduce total inventory to $118M (19% reduction) while improving fill rates from 95.8% to 98.4%.

The ML forecasting ensemble (Random Forest + ARIMA) achieves 92% accuracy by capturing complex demand patterns including seasonality, promotional effects, and vehicle age dependencies. The system identified $18M in slow-moving inventory (>365 days on hand) for liquidation and optimized reorder points for fast-moving items to eliminate stockouts.

Annual benefit: $6.75M in carrying cost savings (25% of $27M freed capital) + $8.4M in revenue increase from improved fill rates = $15.15M total impact.`,
      recommendations: [
        'Reduce total inventory from $145M to $118M (free $27M in capital)',
        'Liquidate $18M in slow-moving inventory (>365 days on hand)',
        'Implement automated reorder point system based on ML forecasts',
        'Increase safety stock for high-velocity SKUs to achieve 98%+ fill rate',
        'Reduce expedited freight by improving forecast accuracy and reorder timing',
        'Invest freed capital in warehouse automation and technology upgrades',
      ],
      items: [
        { sku: 'BRK-45892', product: 'Brake Pad Set', current: 2400, optimal: 1800, reorder: 450, eoq: 600 },
        { sku: 'FLT-23456', product: 'Oil Filter', current: 8500, optimal: 6200, reorder: 1200, eoq: 2000 },
        { sku: 'SPK-78901', product: 'Spark Plug', current: 15000, optimal: 12000, reorder: 2500, eoq: 4000 },
        { sku: 'BAT-34567', product: 'Battery', current: 450, optimal: 380, reorder: 80, eoq: 120 },
        { sku: 'TIR-90123', product: 'Tire Set', current: 280, optimal: 320, reorder: 60, eoq: 100 },
      ],
    };
  }

  async optimizePayables(): Promise<PayablesResult> {
    await delay(1900);
    
    return {
      status: 'optimal',
      savings: 2430000,
      story: modelStories.payables,
      model_info: {
        algorithm: 'Dynamic Programming',
        confidence: 0.95,
      },
      input_summary: {
        monthly_payables: '$42M',
        vendors: '1,200',
        invoices_per_month: '15,000',
        current_dpo: '38 days',
        discount_opportunities: '$3M annually',
      },
      llm_explanation: `The Dynamic Programming optimization model determines the optimal payment date for each invoice, balancing early payment discounts against working capital preservation.

Key findings: FreshBite was capturing only 40% of available early payment discounts, missing $1.8M annually. The optimization increased discount capture to 94%, saving $2.43M annually. Simultaneously, the model extended DPO from 38 to 47 days, freeing $11.5M in working capital that can be invested at 4.2% yield (generating $483K annually).

The DP approach finds the globally optimal payment sequence by evaluating millions of possible schedules. Each payment decision affects future cash availability, which affects future payment decisions - exactly the type of sequential, interdependent problem that DP solves optimally.

Total annual benefit: $2.43M (discounts) + $483K (investment income) + $168K (penalty reduction) = $3.08M.`,
      recommendations: [
        'Capture 94% of early payment discounts (up from 40%) - save $2.43M annually',
        'Extend DPO from 38 to 47 days - free $11.5M in working capital',
        'Invest freed capital at 4.2% average yield - generate $483K annually',
        'Implement automated payment scheduling based on DP optimization',
        'Maintain strategic vendor relationships with predictable payment patterns',
        'Negotiate improved discount terms with high-volume vendors',
      ],
      payments: [
        { vendor: 'Sysco Foods', amount: 850000, due: '2025-11-15', recommended: '2025-11-05', discount: 17000 },
        { vendor: 'US Foods', amount: 720000, due: '2025-11-18', recommended: '2025-11-08', discount: 14400 },
        { vendor: 'Performance Food Group', amount: 580000, due: '2025-11-20', recommended: '2025-11-20', discount: 0 },
        { vendor: 'Gordon Food Service', amount: 450000, due: '2025-11-22', recommended: '2025-11-12', discount: 9000 },
        { vendor: 'Shamrock Foods', amount: 380000, due: '2025-11-25', recommended: '2025-11-25', discount: 0 },
      ],
    };
  }

  async runScenario(): Promise<ScenarioResult> {
    await delay(2400);
    
    return {
      status: 'completed',
      scenarios: 10000,
      var_95: -23000000,
      cvar_95: -31000000,
      expected: 42000000,
      best_case: 89000000,
      worst_case: -45000000,
      story: modelStories.scenario,
      model_info: {
        algorithm: 'Monte Carlo Simulation with Latin Hypercube Sampling',
        confidence: 0.95,
      },
      input_summary: {
        investment: '$180M',
        project_life: '25 years',
        scenarios: '10,000',
        risk_factors: 'Electricity prices, regulatory policy, technology costs, competition',
        sampling_method: 'Latin Hypercube',
      },
      llm_explanation: `Monte Carlo simulation with 10,000 scenarios reveals the full distribution of outcomes for the $180M solar farm investment. The analysis provides critical risk insights beyond deterministic base case projections.

Key findings: 68% probability of achieving >12% IRR (not the assumed certainty). 95% Value at Risk (VaR) of -$23M means there's a 5% chance of losing more than $23M. Conditional VaR (CVaR) of -$31M quantifies the average loss in worst 5% of scenarios.

Sensitivity analysis identified electricity price volatility as the dominant risk driver (correlation 0.78 with NPV). A 10-year power purchase agreement (PPA) for 60% of capacity could reduce VaR by 45%, transforming the risk profile. The option to expand capacity in year 3 adds $8M in real option value.

Based on these insights, SolarVista structured the investment with: (1) 10-year PPA for 60% of capacity, (2) expansion optionality, (3) technology upgrade provisions. Actual IRR achieved: 14.1% (vs. 15.2% projected), outperforming previous projects.`,
      recommendations: [
        'Negotiate 10-year PPA for 60% of capacity to reduce electricity price risk',
        'Structure investment with expansion optionality (adds $8M real option value)',
        'Implement hedging strategy for remaining 40% merchant exposure',
        'Establish contingency reserves for worst-case scenarios ($31M CVaR)',
        'Monitor key risk drivers quarterly and adjust strategy as needed',
        'Consider technology upgrade provisions to mitigate degradation risk',
      ],
    };
  }

  async optimizeTax(): Promise<TaxResult> {
    await delay(2600);
    
    return {
      status: 'optimal',
      current_etr: 28.5,
      optimized_etr: 23.2,
      savings: 127000000,
      story: modelStories.tax,
      model_info: {
        algorithm: 'Mixed-Integer Nonlinear Programming (MINLP)',
        solver: 'IPOPT',
        variables: 84,
        constraints: 156,
        iterations: 2847,
        confidence: 0.89,
      },
      input_summary: {
        revenue: '$2.4B',
        countries: '45',
        legal_entities: '12',
        current_etr: '28.5%',
        current_tax: '$684M annually',
      },
      llm_explanation: `The MINLP optimization model identifies the optimal global profit allocation across 12 legal entities in 45 countries while respecting transfer pricing arm's length requirements, BEPS substance rules, and tax treaty networks.

Key findings: CloudTech's current structure evolved organically without systematic optimization. The US parent retains too much profit (taxed at 21%), while Ireland IP holding structure (12.5% rate) and Singapore regional HQ are underutilized. The optimization identified opportunities to reduce global ETR from 28.5% to 23.2% (5.3 percentage points).

Recommended restructuring: (1) Reallocate $180M profit to Ireland IP holding (save $15.3M annually), (2) Increase Singapore regional HQ allocation by $95M (save $3.8M annually), (3) Optimize transfer pricing within arm's length ranges (save $8.2M annually), (4) Utilize tax treaty benefits for withholding tax reduction (save $4.1M annually).

Total annual savings: $127M. NPV over 10 years: $1.02B. Maintained 100% BEPS compliance with economic substance score of 0.89 (target: >0.80).`,
      recommendations: [
        'Reallocate $180M profit to Ireland IP holding structure (save $15.3M annually)',
        'Increase Singapore regional HQ profit allocation by $95M (save $3.8M annually)',
        'Optimize transfer pricing within arm\'s length ranges (save $8.2M annually)',
        'Utilize tax treaty benefits for withholding tax reduction (save $4.1M annually)',
        'Ensure economic substance requirements met in all jurisdictions (score: 0.89)',
        'Implement country-by-country reporting and BEPS compliance monitoring',
      ],
      allocations: [
        { country: 'United States', profit: 720000000, rate: 21.0, tax: 151200000 },
        { country: 'Ireland', profit: 580000000, rate: 12.5, tax: 72500000 },
        { country: 'Singapore', profit: 420000000, rate: 17.0, tax: 71400000 },
        { country: 'United Kingdom', profit: 280000000, rate: 19.0, tax: 53200000 },
        { country: 'Germany', profit: 220000000, rate: 30.0, tax: 66000000 },
        { country: 'Other', profit: 180000000, rate: 25.0, tax: 45000000 },
      ],
    };
  }

  async optimizePricing(params: {
    company_id: string;
    optimization_objective: string;
    min_margin: number;
    max_discount: number;
  }): Promise<any> {
    await delay(2400);
    
    const numProducts = 50;
    const recommendations = [];
    
    // Generate sample recommendations
    for (let i = 0; i < numProducts; i++) {
      const currentPrice = 50 + Math.random() * 150;
      const priceChange = (Math.random() - 0.5) * 0.3; // -15% to +15%
      const optimalPrice = currentPrice * (1 + priceChange);
      const elasticity = -0.8 - Math.random() * 2.7; // -0.8 to -3.5
      
      recommendations.push({
        product_id: `P${String(i + 1).padStart(3, '0')}`,
        product_name: `Product ${i + 1}`,
        category: ['Electronics', 'Apparel', 'Home Goods', 'Food & Beverage'][Math.floor(Math.random() * 4)],
        current_price: Math.round(currentPrice * 100) / 100,
        optimal_price: Math.round(optimalPrice * 100) / 100,
        price_change_pct: Math.round(priceChange * 10000) / 100,
        revenue_lift: Math.round((optimalPrice - currentPrice) * (100 + Math.random() * 200)),
        margin_pct: Math.round((1 - (currentPrice * 0.6) / optimalPrice) * 10000) / 100,
        elasticity: Math.round(elasticity * 100) / 100,
      });
    }
    
    // Sort by revenue lift
    recommendations.sort((a, b) => b.revenue_lift - a.revenue_lift);
    
    const totalRevenueLift = recommendations.reduce((sum, r) => sum + r.revenue_lift, 0);
    const avgPriceChange = recommendations.reduce((sum, r) => sum + Math.abs(r.price_change_pct), 0) / numProducts;
    
    return {
      status: 'optimal',
      solve_time_seconds: 12.5,
      metrics: {
        revenue_lift: totalRevenueLift,
        revenue_lift_pct: 15.8,
        profit_lift: totalRevenueLift * 0.65,
        profit_lift_pct: 22.3,
        num_products: numProducts,
        num_recommendations: numProducts,
        avg_price_change_pct: avgPriceChange,
      },
      recommendations,
      distribution: {
        price_decreases: recommendations.filter(r => r.price_change_pct < -1).length,
        no_change: recommendations.filter(r => Math.abs(r.price_change_pct) <= 1).length,
        price_increases: recommendations.filter(r => r.price_change_pct > 1).length,
        avg_decrease: -8.5,
        avg_increase: 12.3,
      },
      elasticity_summary: {
        highly_elastic: recommendations.filter(r => r.elasticity < -2.5).length,
        elastic: recommendations.filter(r => r.elasticity >= -2.5 && r.elasticity < -1.5).length,
        unit_elastic: recommendations.filter(r => r.elasticity >= -1.5 && r.elasticity < -0.8).length,
        inelastic: recommendations.filter(r => r.elasticity >= -0.8).length,
      },
      model_info: {
        algorithm: 'Differential Evolution Optimization',
        solver: 'SciPy',
        variables: numProducts,
        iterations: 100,
        confidence: 0.87,
      },
      input_summary: {
        company_id: params.company_id,
        objective: params.optimization_objective,
        min_margin: `${(params.min_margin * 100).toFixed(0)}%`,
        max_discount: `${(params.max_discount * 100).toFixed(0)}%`,
        products_analyzed: numProducts,
      },
    };
  }
}

export const api = new EnhancedAPI();
